// Admin overview — slim orchestrator. Sub-components live in /pages/admin/overview/.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Globe, Activity, Users, Plus, Loader2 } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { StatCard } from './overview/StatCard';
import { GradingPipelineWidget } from './overview/GradingPipelineWidget';
import { CustomerDomainsList } from './overview/CustomerDomainsList';
import { SchedulerHealthCard } from './overview/SchedulerHealthCard';
import { DeployHealthCard } from './overview/DeployHealthCard';
import DigestPreviewCard from './overview/DigestPreviewCard';
import RebaselineReviewCard from './overview/RebaselineReviewCard';
import OpsControlsCard from './overview/OpsControlsCard';
import { AttributionInsightsCard } from './overview/AttributionInsightsCard';
import { SubscriptionsCard } from './overview/SubscriptionsCard';
import RegionalRollupCard from './overview/RegionalRollupCard';
import { statCardColor } from './overview/overviewHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_INTERVAL = 30; // seconds

const computeTotals = (domains) => {
  const totalCalls = domains.reduce((s, d) => s + (d.total_calls || 0), 0);
  const totalGraded = domains.reduce((s, d) => s + (d.graded ?? ((d.analyzed || 0) + (d.escalated || 0))), 0);
  const totalGradeable = domains.reduce((s, d) => s + (d.gradeable || 0), 0);
  // Sum of "gradeable-total" per domain (excludes short/no-transcript from denom).
  const totalGradeableTotal = domains.reduce(
    (s, d) => s + (d.gradeable_total ?? ((d.analyzed || 0) + (d.escalated || 0) + (d.pending || 0))),
    0,
  );
  const pendingTotal = domains.reduce((s, d) => s + (d.pending || 0), 0);
  const activeDomains = domains.filter(d => d.status === 'active').length;
  const domainsGrading = domains.filter(d => (d.gradeable || 0) > 0).length;
  // Overall % now uses the honest denominator (gradeable-total, not raw total).
  const overallPct = totalGradeableTotal > 0
    ? Math.round((totalGraded / totalGradeableTotal) * 100)
    : 0;
  return {
    totalCalls, totalGraded, totalGradeable, totalGradeableTotal, pendingTotal,
    activeDomains, domainsGrading, overallPct,
  };
};

const AdminOverviewPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [windowDays, setWindowDays] = useState(null);   // null = all time

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/sync-status`, {
        headers: { Authorization: `Bearer ${token}` },
        params: windowDays ? { days: windowDays } : {},
      });
      setDomains(r.data);
      setLastRefreshed(new Date());
      setCountdown(REFRESH_INTERVAL);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
    }
    if (showRefreshing) setRefreshing(false);
    setLoading(false);
  }, [token, windowDays]);

  useEffect(() => {
    fetchData();
    const refreshTimer = setInterval(() => fetchData(), REFRESH_INTERVAL * 1000);
    const countdownTimer = setInterval(() => setCountdown(c => Math.max(c - 1, 0)), 1000);
    return () => {
      clearInterval(refreshTimer);
      clearInterval(countdownTimer);
    };
  }, [fetchData]);

  const totals = useMemo(() => computeTotals(domains), [domains]);

  const openDomain = (id) => navigate(`/admin/domains/${id}`);
  const goToNewDomain = () => navigate('/admin/domains/new');

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Overview</h1>
            <p className="text-white/40 text-sm mt-1">Platform health and customer summary</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/admin/impersonation-log')}
              className="text-xs font-semibold px-3 py-2 bg-white/5 hover:bg-white/10 text-amber-300 rounded-lg inline-flex items-center gap-1.5 border border-white/10"
              data-testid="admin-impersonation-log-btn"
              title="Compliance audit log of every 'View as user' session"
            >
              Impersonation Log
            </button>
            <button
              onClick={goToNewDomain}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg transition-colors"
              data-testid="add-domain-btn-header"
            >
              <Plus className="w-4 h-4" /> Add Domain
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-white/30 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                icon={Globe} label="Active Domains" value={totals.activeDomains}
                color={statCardColor('teal')}
                testId="stat-card-active-domains"
                onClick={() => navigate('/admin/domains')}
              />
              <StatCard
                icon={Activity} label="Total Calls Synced" value={totals.totalCalls.toLocaleString()}
                color={statCardColor('blue')}
                testId="stat-card-total-calls-synced"
                onClick={() => navigate('/admin/call-master')}
              />
              <StatCard
                icon={Users} label="Customer Domains" value={domains.length}
                color={statCardColor('violet')}
                testId="stat-card-customer-domains"
                onClick={() => navigate('/admin/domains')}
              />
            </div>

            <SchedulerHealthCard token={token} />

            <DeployHealthCard />

            <AttributionInsightsCard days={30} />

            <SubscriptionsCard />

            <DigestPreviewCard />
            <RebaselineReviewCard />

            <OpsControlsCard token={token} />

            <GradingPipelineWidget
              domains={domains}
              totals={totals}
              lastRefreshed={lastRefreshed}
              countdown={countdown}
              refreshing={refreshing}
              onRefresh={() => fetchData(true)}
              onRowClick={openDomain}
              windowDays={windowDays}
              onWindowChange={setWindowDays}
            />

            <RegionalRollupCard token={token} />

            <CustomerDomainsList
              domains={domains}
              onAdd={goToNewDomain}
              onOpenDomain={openDomain}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOverviewPage;
